import axios from "axios";
import { AppResponse } from "../models/Response";
import { Util } from "../Util";
import { Order } from "models/Order";
import { OrderResponse } from "models/orderResponse";
import { OrderListWrapper } from "models/Order";
export interface Count {
  count?: number;
}

export interface OrderSubmissionRequest {
  idempotencyKey: string;
  traceId: string;
}

export class OrderService {
  public static async createOrder(
    order: Order,
    submission?: OrderSubmissionRequest
  ): Promise<AppResponse<OrderResponse>> {
    const ep = Util.apiAuthUrl("order/create");
    const traceId =
      submission?.traceId || Util.createClientTraceId("web-customer-order");
    const idempotencyKey =
      submission?.idempotencyKey ||
      Util.createClientTraceId("web-customer-order-submit");
    const startedAt = Date.now();
    const res = await axios.post<Order, AppResponse<OrderResponse>>(ep, order, {
      headers: {
        "X-Client-Trace-Id": traceId,
        "X-Idempotency-Key": idempotencyKey,
      },
    });
    Util.logOrderSubmitTiming({
      endpoint: "POST /api/auth/order/create",
      traceId,
      elapsedMs: Date.now() - startedAt,
      success: !!res?.success,
      role: "customer",
      error: res?.error,
    });
    return res;
  }

  public static async orderDetails(order: Order): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("order/details");
    const res = await axios.post<Order, AppResponse<string>>(ep, order);
    return res;
  }

  public static async createStripeSession(data: any): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl(`checkout-session`);
    const res = await axios.post<Order, AppResponse<string>>(ep, data);
    return res;
  }

  public static async getOrderById(orderId: string): Promise<AppResponse<OrderResponse>> {
    if (!orderId || orderId === "undefined" || orderId === "null") {
      return {
        success: false,
        data: undefined as any,
        message: "",
        error: "A valid order ID is required.",
        errorCode: "INVALID_ORDER_ID",
      } as AppResponse<OrderResponse>;
    }
    const ep = Util.apiAuthUrl(`order/${orderId}`);
    const res = await axios.get<Order, AppResponse<OrderResponse>>(ep);
    return res;
  }

  public static async genarateOrderPDF(orderId: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`order/pdf/${orderId}`);
    const res = await axios.get<Order, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }
  public static async generateShippingLabelPDF(orderId: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`shipping-label/pdf/${orderId}`);
    const res = await axios.get<Order, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }

  public static async genarateOrderReceiptPDF(orderId: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`order/receipt/pdf/${orderId}`);
    const res = await axios.get<Order, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }

  public static async getOrdersByDuration(data: any, limit?: number, offset?: number): Promise<AppResponse<Order[]>> {
    const url = Util.apiAuthUrl(`order/duration/list/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<Order[]>>(url, data);
  }

  public static async getOrdersSearchBystatus(data: any, limit?: number): Promise<AppResponse<Order[]>> {
    const url = Util.apiAuthUrl(`order/orderlistbysearch/` + "1/" + limit);
    return await axios.post<void, AppResponse<Order[]>>(url, data);
  }

  public static async getOrdersByPaymentStatusBySearch(data: any, limit?: number): Promise<AppResponse<OrderResponse[]>> {
    const url = Util.apiAuthUrl(`order/searchpaymentstatus/` + "1/" + limit);
    return await axios.post<void, AppResponse<OrderResponse[]>>(url, data);
  }

  public static async getOrdersByStatus(data: any, limit?: number, offset?: number): Promise<AppResponse<Order[]>> {
    const url = Util.apiAuthUrl(`order/list/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<Order[]>>(url, data);
  }

  public static async getOrdersByPaymentStatus(data: any, limit?: number, offset?: number): Promise<AppResponse<OrderResponse[]>> {
    const url = Util.apiAuthUrl(`order/paymentstatus/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<OrderResponse[]>>(url, data);
  }

  public static async getOrderCountByUserId(userId?: string): Promise<AppResponse<Count>> {
    const ep = Util.apiAuthUrl(`order/count/${userId}`);
    const res = await axios.get<number, AppResponse<Count>>(ep);
    return res;
  }

  public static async getPendingStatusOfOrders(userId?: string): Promise<AppResponse<boolean>> {
    const ep = Util.apiAuthUrl(`order/pending-status/${userId}`);
    const res = await axios.get<void, AppResponse<boolean>>(ep);
    return res;
  }

  public static async getLiveOrders(): Promise<AppResponse<OrderListWrapper>> {
    const ep = Util.apiAuthUrl(`order/live-order-list`);
    const res = await axios.get<void, AppResponse<OrderListWrapper>>(ep);
    return res;
  }

  public static async validatePromoCode(promoCode: string, orderId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("order/promoCode");
    const res = await axios.post<any, AppResponse<any>>(ep, {
      code: promoCode,
      orderId: orderId
    });
    return res;
  }

  public static async getPromoEnabled(): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("getPromoEnabled");
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }

  public static async generateManifestPDF(orderIds: string): Promise<AppResponse<ArrayBuffer>> {
    const url = Util.apiAuthUrl('orders/manifest-pdf');

    // POST the orderIds in the body
    const response = await axios.post<never, AppResponse<ArrayBuffer>>(
      url,
      { orderIds },
      { responseType: 'arraybuffer' }
    );

    return response;
  }

  public static async createBulkOrder(pickupData: any, deliveryData: any, pickupDateTime: string, deliveryDateTime: string, settingsType: string, userId?: string, bulkRequestId?: string): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("order/bulkOrdercCreate");
    const payload = { pickup: pickupData, delivery: deliveryData, pickupDateTime, deliveryDateTime, bulkRequestId, settingsType, userId };
    const res = await axios.post<AppResponse<string>>(ep, payload);
    return res.data;
  }

  public static async uploadBulkFile(pickupData: any, deliveries: any[], pickupDateTime: string, deliveryDateTime: string, settingsType: string, bulkRequestId?: string): Promise<{
    bulkRequestId: string; orderIds: string[]; total: number; prices: number[], totalDistance: number[];
  }> {
    const ep = Util.apiAuthUrl("order/bulkRequestFileUpload");
    const payload = {
      pickup: pickupData, deliveries, pickupDateTime, deliveryDateTime, bulkRequestId, settingsType
    };
    const { data } = await axios.post(ep, payload);
    return data;
  }

  public static async getCustomerBulkRequestsWithOrders(userId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`order/bulkRequest/${userId}`);
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }

  public static async getCompletedCustomerBulkRequests(userId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`order/completedBulkRequest/${userId}`);
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }

  public static async submitBulkRequest(bulkRequestId: any, userId: any,): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("order/bulkRequestSubmit");
    const payload = { userId, bulkRequestId, };

    const res = await axios.post<AppResponse<string>>(ep, payload);
    return res.data

  }

  public static async generateBulkOrderPdf(bulkRequestId: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`order/bulkOrderPdf/${bulkRequestId}`);
    const res = await axios.get<Order, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer",
    });
    return res;
  }

  public static async bulkRequestOrdersDelete(data: any): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("order/bulkRequestOrdersDelete");
    const res = await axios.post<Order, AppResponse<string>>(ep, data);
    return res;
  }



}
